"use client";

import React from "react";
import { ShieldCheck, House, FileCheck, ChartBar, MapPin, TrendingUp } from "lucide-react";
import { Card, CardHeader, CardTitle, CardContent } from "@/components/ui/card";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
const standards = [{
  title: "CPI-Linked Income",
  description: "Inflation-hedged returns that grow with the economy. Your rental income is linked to the Consumer Price Index, ensuring purchasing power preservation.",
  icon: TrendingUp,
  highlight: true
}, {
  title: "FRI Lease Terms",
  description: "Full Repairing and Insuring leases mean zero maintenance costs for you. The housing association handles all upkeep, insurance, and repairs.",
  icon: FileCheck,
  highlight: false
}, {
  title: "Fully Managed",
  description: "A truly hands-free investment experience. Our professional partners manage tenant placement, property care, and daily operations without investor input.",
  icon: ShieldCheck,
  highlight: false
}, {
  title: "Government Backed",
  description: "Secure your capital with income streams ultimately funded by central government budgets, providing institutional-grade security for your portfolio.",
  icon: ChartBar,
  highlight: false
}, {
  title: "Social Impact ESG",
  description: "Invest with purpose by helping solve the UK's 1.1M social housing waiting list while generating superior risk-adjusted financial returns.",
  icon: House,
  highlight: false
}, {
  title: "Asset-Backed Security",
  description: "Direct ownership of UK brick-and-mortar assets. Your investment is secured by physical property with significant long-term capital growth potential.",
  icon: MapPin,
  highlight: false
}];
export default function OurEliteStandards() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section data-section-id="122917" id="the-social-housing-investment-advantage" className="bg-background text-foreground py-24 lg:py-32 overflow-hidden">
      <div className="container mx-auto px-6 md:px-8 max-w-7xl">
        <div ref={ref} className={`flex flex-col items-center text-center mb-16 transition-all duration-1000 ease-out ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
          <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold tracking-tight text-foreground mb-6 font-default">
            The Investment Advantage
          </h2>
          <p className="text-lg md:text-xl text-muted-foreground font-default max-w-2xl leading-relaxed">
            Discover why institutional investors are shifting capital toward social housing—a unique asset class combining high yields with government-backed security.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
          {standards.map((standard, i) => {
          const Icon = standard.icon;
          const delay = i * 150;
          return <div key={standard.title} data-index={i} className={`transition-all duration-700 ease-out fill-mode-both ${isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`} style={{
            transitionDelay: `${delay}ms`
          }}>
                <Card className={`h-full flex flex-col transition-all duration-500 hover:-translate-y-2 bg-gradient-to-br from-secondary to-secondary/90 border-primary/20 hover:border-primary/60 group shadow-lg ${standard.highlight ? "ring-1 ring-primary/30 shadow-primary/5 relative overflow-hidden" : ""}`}>
                  {standard.highlight && <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary/40 via-primary to-primary/40" />}
                  <CardHeader className="pb-4">
                    <div className="mb-4 inline-flex h-12 w-12 items-center justify-center rounded-lg bg-primary/10 group-hover:bg-primary/20 transition-colors">
                      <Icon className="h-6 w-6 text-primary" strokeWidth={1.5} />
                    </div>
                    <CardTitle className="text-xl font-bold tracking-tight font-default !text-[hsl(0,0%,100%)]">
                      {standard.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="pt-0 flex-grow">
                    <p className="text-muted-foreground text-[14px] font-default leading-relaxed">
                      {standard.description}
                    </p>
                  </CardContent>
                </Card>
              </div>;
        })}
        </div>
      </div>
    </section>;
}